import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PageHeader from "../pages/PageHeader.jsx";
import {
  fetchChatUsers,
  fetchMyChats,
  fetchChatHistory,
  fetchChannels,
  createChannel,
  fetchUnreadCounts,
  markChatAsRead,
} from "../features/chats/chatThunks.js";
import "../styles/chats/chatsidebar.css";
import { useNavigate } from "react-router-dom";

const ChatSidebar = ({ chats = [], activeId, onSelect, loading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  const chatUsersRaw = useSelector((state) => state.chat.chatUsers);
  const chatError = useSelector((state) => state.chat.error);
  const channelsState = useSelector((state) => state.chat.channels) || [];
  const unreadByChatId = useSelector((state) => state.chat.unreadByChatId) || {};

  const chatUsers = Array.isArray(chatUsersRaw) ? chatUsersRaw : [];
  const allChats = Array.isArray(chats) ? chats : [];

  const chatUsersById = useMemo(() => {
    const map = new Map();
    for (const user of chatUsers) {
      map.set(String(user.id), user);
    }
    return map;
  }, [chatUsers]);

  const getUserDisplayName = useCallback((user) => {
    if (!user) return "Usuario";
    const firstName = (user.first_name || "").trim();
    const lastName = (user.last_name || "").trim();
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    return user.username || "Usuario";
  }, []);

  // ---------------------------
  // Modal: crear canal
  // ---------------------------
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [createChannelForm, setCreateChannelForm] = useState({ name: "", topic: "" });
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [createChannelError, setCreateChannelError] = useState(null);

  useEffect(() => {
    if (!token || !currentUser) return;
    dispatch(fetchChatUsers());
    dispatch(fetchMyChats());
    dispatch(fetchChannels());
    dispatch(fetchUnreadCounts());
  }, [dispatch, currentUser, token]);

  const closeCreateChannelModal = useCallback(() => {
    if (creatingChannel) return;
    setCreateChannelOpen(false);
    setCreateChannelForm({ name: "", topic: "" });
    setCreateChannelError(null);
  }, [creatingChannel]);

  useEffect(() => {
    if (!createChannelOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (creatingChannel) return;
      closeCreateChannelModal();
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [createChannelOpen, creatingChannel, closeCreateChannelModal]);

  // ---------------------------
  // Chats derivados
  // ---------------------------
  const dmChats = useMemo(() => allChats.filter((chat) => !chat?.is_group), [allChats]);

  const groupChats = useMemo(() => {
    if (channelsState.length > 0) return channelsState;
    return allChats.filter((chat) => chat?.is_group);
  }, [channelsState, allChats]);

  // Mapa: peerId -> dmChat (para no hacer find() mil veces)
  const dmChatByPeerId = useMemo(() => {
    const map = new Map();
    const myId = currentUser?.id;
    if (!myId) return map;

    for (const chat of dmChats) {
      const participants = Array.isArray(chat?.participants) ? chat.participants : [];
      const otherParticipant = participants.find((p) => p?.id && p.id !== myId);

      const peerId = otherParticipant?.id || chat?.receiver?.id || chat?.sender?.id || null;
      if (peerId && peerId !== myId) {
        map.set(String(peerId), chat);
      }
    }

    return map;
  }, [dmChats, currentUser?.id]);

  // ✅ FIX: IDs de usuarios que YA tienen DM -> deben desaparecer de "Usuarios disponibles"
  const directMessageUserIds = useMemo(() => {
    return new Set(Array.from(dmChatByPeerId.keys()));
  }, [dmChatByPeerId]);

  const isInitialLoading = useMemo(() => {
    return loading && allChats.length === 0 && chatUsers.length === 0;
  }, [loading, allChats.length, chatUsers.length]);

  // Usuarios disponibles para iniciar DMs (solo profesionales, sin pacientes) + ✅ excluye los que ya tienen DM
  const visibleUsers = useMemo(() => {
    if (!currentUser) return [];
    const myId = currentUser.id;

    if (currentUser.role === "patient") return [];

    const base = chatUsers.filter((user) => {
      if (!user?.id) return false;
      if (user.id === myId) return false;
      if (user.role === "patient") return false;
      if (directMessageUserIds.has(String(user.id))) return false; // ✅ aquí está el fix
      return true;
    });

    return [...base].sort((a, b) => {
      const aFull = `${a.first_name || ""} ${a.last_name || ""}`.trim();
      const bFull = `${b.first_name || ""} ${b.last_name || ""}`.trim();

      const an = (aFull || a.username || "").toLowerCase();
      const bn = (bFull || b.username || "").toLowerCase();

      return an.localeCompare(bn);
    });
  }, [chatUsers, currentUser, directMessageUserIds]);

  const openOrCompose = useCallback(
    async (receiverId) => {
      if (!currentUser?.id || !receiverId) return;

      const reusable = dmChatByPeerId.get(String(receiverId)) || null;

      if (reusable) {
        await dispatch(fetchChatHistory(reusable.id));
        await dispatch(markChatAsRead(reusable.id));
        onSelect?.(reusable.id);
        return;
      }

      navigate("/chats", { state: { receiverId } });
    },
    [dispatch, currentUser, dmChatByPeerId, onSelect, navigate]
  );

  // ---------------------------
  // Crear canal (modal)
  // ---------------------------
  const openCreateChannelModal = useCallback(() => {
    setCreateChannelError(null);
    setCreateChannelForm({ name: "", topic: "" });
    setCreateChannelOpen(true);
  }, []);

  const handleCreateChannelSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const name = String(createChannelForm.name || "").trim();
      const topic = String(createChannelForm.topic || "").trim();

      if (!currentUser) {
        setCreateChannelError("No hay usuario autenticado.");
        return;
      }

      if (!name) {
        setCreateChannelError("El nombre del canal es obligatorio.");
        return;
      }

      setCreateChannelError(null);
      setCreatingChannel(true);

      try {
        const action = await dispatch(
          createChannel({
            name,
            topic,
            participantIds: [], // el backend mete al creador
          })
        );

        if (createChannel.fulfilled.match(action)) {
          const newChannel = action.payload;

          setCreateChannelOpen(false);
          setCreateChannelForm({ name: "", topic: "" });
          setCreateChannelError(null);

          dispatch(fetchChannels());
          onSelect?.(newChannel.id);
          await dispatch(fetchChatHistory(newChannel.id));
          await dispatch(markChatAsRead(newChannel.id));
          return;
        }

        const err = action.payload || { detail: "No se pudo crear el canal." };
        setCreateChannelError(err.detail || "No se pudo crear el canal.");
      } catch (err) {
        setCreateChannelError("Error al crear canal.");
      } finally {
        setCreatingChannel(false);
      }
    },
    [dispatch, createChannelForm, currentUser, onSelect]
  );

  return (
    <div className="chat-sidebar" role="complementary" aria-label="Barra lateral de chats">
      <div className="chat-sidebar-top">
        <PageHeader />
      </div>

      {/* Canales */}
      <div className="chat-sidebar-header chat-sidebar-header-row">
        <h3>Canales</h3>
        <button
          type="button"
          className="chat-sidebar-create-channel"
          onClick={openCreateChannelModal}
        >
          + Crear canal
        </button>
      </div>

      {/* MODAL CREAR CANAL */}
      {createChannelOpen && (
        <div className="ui-modal-overlay" onClick={closeCreateChannelModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Crear canal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Crear canal</h3>
            </div>

            <form onSubmit={handleCreateChannelSubmit}>
              <div className="ui-modal-body">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="channelName">
                    Nombre del canal (sin #)
                  </label>
                  <input
                    id="channelName"
                    className="ui-input"
                    type="text"
                    value={createChannelForm.name}
                    onChange={(e) =>
                      setCreateChannelForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Ej: coordinación"
                    autoFocus
                    disabled={creatingChannel}
                  />
                  <div className="ui-help">
                    Se mostrará como: #{createChannelForm.name.trim() || "canal"}
                  </div>
                </div>

                <div className="ui-field">
                  <label className="ui-label" htmlFor="channelTopic">
                    Tema (opcional)
                  </label>
                  <input
                    id="channelTopic"
                    className="ui-input"
                    type="text"
                    value={createChannelForm.topic}
                    onChange={(e) =>
                      setCreateChannelForm((prev) => ({
                        ...prev,
                        topic: e.target.value,
                      }))
                    }
                    placeholder="Ej: avisos del equipo"
                    disabled={creatingChannel}
                  />
                </div>

                {createChannelError ? (
                  <div className="ui-modal-error">{createChannelError}</div>
                ) : null}
              </div>

              <div className="ui-modal-footer">
                <button
                  type="button"
                  className="ghost btn-sm"
                  onClick={closeCreateChannelModal}
                  disabled={creatingChannel}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary btn-sm"
                  disabled={creatingChannel}
                >
                  {creatingChannel ? "Creando…" : "Crear canal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isInitialLoading && <div className="chat-sidebar-status">Cargando...</div>}

      {!isInitialLoading && Array.isArray(groupChats) && groupChats.length === 0 && (
        <div className="chat-sidebar-empty">Aún no estás en ningún canal.</div>
      )}

      {Array.isArray(groupChats) && groupChats.length > 0 && (
        <ul className="chat-thread-list">
          {groupChats.map((channel) => {
            const isActive = Number(activeId) === Number(channel.id);
            const title = channel.name || "Canal sin nombre";
            const participantsCount = Array.isArray(channel.participants)
              ? channel.participants.length
              : 0;
            const subtitle =
              channel.topic ||
              `${participantsCount} participante${participantsCount === 1 ? "" : "s"}`;

            const unreadCount =
              unreadByChatId[channel.id] || unreadByChatId[String(channel.id)] || 0;

            const initials = (() => {
              const base = String(title).trim();
              if (!base) return "#";
              const [a = ""] = base.split(/\s+/);
              return (a[0] || "#").toUpperCase();
            })();

            return (
              <li key={channel.id} className={`chat-thread${isActive ? " active" : ""}`}>
                <button
                  type="button"
                  className="thread-button thread-button--channel"
                  aria-current={isActive ? "true" : "false"}
                  aria-label={`Abrir canal #${title}`}
                  onClick={() => {
                    onSelect?.(channel.id);
                    dispatch(markChatAsRead(channel.id));
                  }}
                >
                  <div className="thread-avatar" aria-hidden="true">
                    <div className="avatar-fallback">{initials}</div>
                  </div>

                  <div className="thread-main">
                    <div className="thread-title">#{title}</div>
                    <div className="thread-meta">{subtitle}</div>
                  </div>

                  {unreadCount > 0 && (
                    <span className="thread-unread-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Mensajes directos */}
      <div className="chat-sidebar-header">
        <h3>Mensajes directos</h3>
      </div>

      {!loading && Array.isArray(dmChats) && dmChats.length === 0 && (
        <div className="chat-sidebar-empty">Aún no tienes chats directos.</div>
      )}

      {Array.isArray(dmChats) && dmChats.length > 0 && (
        <ul className="chat-thread-list">
          {dmChats.map((chat) => {
            const meId = currentUser?.id;

            const participants = Array.isArray(chat?.participants) ? chat.participants : [];
            const peerFromParticipants = participants.find((p) => p?.id && p.id !== meId) || null;
            const peerRaw = peerFromParticipants || chat.receiver || chat.sender || null;

            const peerFull =
              (peerRaw?.id ? chatUsersById.get(String(peerRaw.id)) : null) || peerRaw;

            const peerName = getUserDisplayName(peerFull);
            const avatarUrl =
              peerFull?.profile_picture_url || peerFull?.profile_picture || null;

            const isActive = Number(activeId) === Number(chat.id);

            const initials = (() => {
              const [a = "", b = ""] = String(peerName).trim().split(/\s+/);
              const chars = (a[0] || "") + (b[0] || "");
              return (chars || "?").toUpperCase();
            })();

            const unreadCount =
              unreadByChatId[chat.id] || unreadByChatId[String(chat.id)] || 0;

            return (
              <li key={chat.id} className={`chat-thread${isActive ? " active" : ""}`}>
                <button
                  type="button"
                  className="thread-button"
                  onClick={() => {
                    onSelect?.(chat.id);
                    dispatch(markChatAsRead(chat.id));
                  }}
                  aria-current={isActive ? "true" : "false"}
                >
                  <div className="thread-avatar" aria-hidden="true">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={peerName} className="avatar-img" />
                    ) : (
                      <div className="avatar-fallback">{initials}</div>
                    )}
                  </div>

                  <div className="thread-main">
                    <div className="thread-title">{peerName}</div>
                    <div className="thread-meta">
                      {chat.updated_at ? new Date(chat.updated_at).toLocaleString() : ""}
                    </div>
                  </div>

                  {unreadCount > 0 && (
                    <span className="thread-unread-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Usuarios disponibles para iniciar DMs */}
      <div className="chat-sidebar-header">
        <h4>Usuarios disponibles</h4>
      </div>

      {chatError && <div className="chat-sidebar-status error">Error: {chatError}</div>}

      <div className="chat-bubbles-wrapper" role="list">
        {visibleUsers.length === 0 ? (
          <p className="no-users">No hay otros usuarios disponibles para chatear.</p>
        ) : (
          visibleUsers.map((user) => {
            const label = getUserDisplayName(user);
            const avatarUrl = user.profile_picture_url || user.profile_picture || null;

            const avatarChar = (
              (user.first_name && user.first_name[0]) ||
              (user.username && user.username[0]) ||
              "?"
            ).toUpperCase();

            return (
              <button
                type="button"
                key={user.id}
                role="listitem"
                className="chat-bubble"
                onClick={() => openOrCompose(user.id)}
                aria-label={`Chatear con ${label}`}
              >
                <div className="chat-avatar" aria-hidden="true">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={label} className="avatar-img" />
                  ) : (
                    avatarChar
                  )}
                </div>
                <div className="chat-info">
                  <strong>{label}</strong>
                  <small>{String(user.role || "").toLowerCase() === "receptionist" ? "coordinación" : (user.role || "")}</small>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;